import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { shouldRefetchFrom, type Fix } from '@utils/preciseFix'
import { adventuresApi } from '@api/endpoints/adventures'
import { markLiveHere } from '../components/player/endingPhase'
import { AdventureListParams, CreateAdventureRequest, UpdateAdventureRequest, AdventureDto, AdventureSearchRequest, AdventureJoinRequest } from '@api/types/adventure'
import { getCachedSearchResults, cacheSearchResults } from '@utils/adventureCache'

// Transform API response to match our expected AdventureDto format
function transformAdventure(apiData: any): AdventureDto {
  // Build location object from top-level latitude/longitude fields
  // Check for both exact location (latitude/longitude) and approximate location (approxLatitude/approxLongitude)
  let location = undefined

  if (apiData.latitude !== undefined && apiData.longitude !== undefined) {
    location = {
      latitude: apiData.latitude,
      longitude: apiData.longitude,
      address: typeof apiData.location === 'string' ? apiData.location : undefined,
    }
  } else if (apiData.approxLatitude !== undefined && apiData.approxLongitude !== undefined) {
    location = {
      latitude: apiData.approxLatitude,
      longitude: apiData.approxLongitude,
      address: typeof apiData.location === 'string' ? apiData.location : undefined,
    }
  }

  // Extract all fields, including editor-specific ones
  const transformed: AdventureDto = {
    id: apiData.id,
    title: apiData.title || apiData.name || '',
    description: apiData.description || apiData.fullDescription || apiData.quickDescription || '',
    fullDescription: apiData.fullDescription || apiData.description || '',
    quickDescription: apiData.quickDescription || '',
    descriptionNarrations: apiData.descriptionNarrations ?? [],
    descriptionNarrationScripts: apiData.descriptionNarrationScripts ?? null,
    // Getting there: an allowlist, so an unlisted field vanishes even when the server sends it.
    externalUrl: apiData.externalUrl ?? undefined,
    externalUrlLabel: apiData.externalUrlLabel ?? undefined,
    contactPhone: apiData.contactPhone ?? undefined,
    contactPhoneLabel: apiData.contactPhoneLabel ?? undefined,
    contactEmail: apiData.contactEmail ?? undefined,
    arrivalNotes: apiData.arrivalNotes ?? null,
    arrivalNotesAudio: apiData.arrivalNotesAudio ?? null,
    difficulty: apiData.difficultyLevel || apiData.difficulty || 1,
    difficultyLevel: apiData.difficultyLevel || apiData.difficulty || 1,
    // Keep numeric status from API (AdventureStatus enum: 1=Draft, 2=Testing, 3=Published, 4=ModeratorReview, 5=Archived, 6=Suspended)
    status: apiData.status ?? 1, // Default to Draft (1) if undefined
    createdBy: apiData.creatorId || apiData.creator?.id || apiData.owner?.entityId || apiData.createdBy || '',
    creatorAccessRevoked: apiData.creatorAccessRevoked ?? false,
    createdByName: apiData.creatorName || apiData.createdByUserName || apiData.creator?.displayName || apiData.owner?.entityName || apiData.createdByName || 'Unknown',
    // Owner entity (new API format)
    owner: apiData.owner,
    createdDate: apiData.createdDate || apiData.createdAt || new Date().toISOString(),
    modifiedDate: apiData.modifiedDate || apiData.updatedDate || apiData.updatedAt || new Date().toISOString(),
    imageUrl: apiData.imageUrl || apiData.pictureUrl,
    thumbnailUrl: apiData.thumbnailUrl || apiData.thumbnailImageUrl,
    coverUrl: apiData.coverUrl || apiData.coverImageUrl,
    thumbnailImageUrl: apiData.thumbnailImageUrl || apiData.thumbnailUrl,
    coverImageUrl: apiData.coverImageUrl || apiData.coverUrl,
    thumbnailImageId: apiData.thumbnailImageId,
    coverImageId: apiData.coverImageId,
    tags: apiData.tags || [],
    participantCount: apiData.participantCount || 0,
    completionCount: apiData.completionCount || 0,
    averageRating: apiData.averageRating || (typeof apiData.rating === 'number' ? apiData.rating : apiData.rating?.overallAverageScore) || undefined,
    ratingCount: apiData.ratingCount,
    rating: typeof apiData.rating === 'object' ? apiData.rating : undefined,
    isPublic: apiData.isPublic !== undefined ? apiData.isPublic : true,
    location,
    // Editor-specific fields
    // API returns categoryId, we map it to mainCategory for internal use
    mainCategory: apiData.categoryId ?? apiData.mainCategory,
    startDate: apiData.startDate,
    endDate: apiData.endDate,
    playerPositions: apiData.playerPositions,
    isEvent: apiData.isEvent,
    isWatched: apiData.isWatched,
    latitude: apiData.latitude,
    longitude: apiData.longitude,
    allowGroups: apiData.allowGroups !== undefined ? apiData.allowGroups : false,
    groupPlayStatus: apiData.groupPlayStatus ?? (apiData.allowGroups ? 1 : 0),
    maxParticipants: apiData.maxParticipants !== undefined ? apiData.maxParticipants : 0,
    // Availability cap + live state from the API
    maxCompletions: apiData.maxCompletions ?? 0,
    capMetric: apiData.capMetric ?? 0,
    currentCapCount: apiData.currentCapCount,
    remainingSpots: apiData.remainingSpots,
    isFull: apiData.isFull,
    isRepeatable: apiData.isRepeatable !== undefined ? apiData.isRepeatable : false,
    // This function is an allowlist: a field it does not name is dropped on the way in, however
    // faithfully the server sent it. Absent reads as on, matching the column's default.
    allowAutoTrail: apiData.allowAutoTrail !== undefined ? apiData.allowAutoTrail : true,
    // Group participation settings
    groupParticipationSettings: apiData.groupParticipationSettings,
    // Silent adventure fields
    isSilentJoin: apiData.isSilentJoin,
    isSilentComplete: apiData.isSilentComplete,
    silentCompleteDurationMinutes: apiData.silentCompleteDurationMinutes,
    showAnswersInJournal: apiData.showAnswersInJournal,
    defaultAudioAutoPlay: apiData.defaultAudioAutoPlay,
    isFavorited: apiData.isFavorited,
    coverImageAttribution: apiData.coverImageAttribution,
    galleryImages: apiData.galleryImages,
    operatingHours: apiData.operatingHours,
    // Offline: capability is computed server-side, permission is the creator's.
    isOfflineCompatible: apiData.isOfflineCompatible,
    allowOfflinePlay: apiData.allowOfflinePlay,
    exitButtonMode: apiData.exitButtonMode,
    // Whether plays are counted, and how (0=None, 1=Leaderboard, 2=CheckIn). This
    // allowlist dropped it, so every consumer saw `undefined`: the detail page's
    // leaderboard tab (which requires Leaderboard) could never appear, the player
    // never treated a session as ranked, and the editor loaded the control as
    // "Not tracked" no matter what the server held.
    statsMode: apiData.statsMode,
    // Clue rules, edited on the Clue Settings page and saved with the rest of the adventure. Not
    // defaulted: an absent value must stay absent so a save cannot zero what the server holds.
    maxCluesAllowed: apiData.maxCluesAllowed,
    cluePenaltySeconds: apiData.cluePenaltySeconds,
    carryCluesPenaltyOnRetry: apiData.carryCluesPenaltyOnRetry,
    fileCount: apiData.fileCount,
    // Revision provenance. This transform is an allowlist, so these must be named here or the
    // detail header silently shows nothing even though the server sent them.
    publishedWorkflowVersion: apiData.publishedWorkflowVersion,
    pendingWorkflowVersion: apiData.pendingWorkflowVersion,
    lastRevisedAt: apiData.lastRevisedAt,
    // Eligibility fields (from search results)
    isEligible: apiData.isEligible,
    meetsDisplayCriteria: apiData.meetsDisplayCriteria,
    meetsJoinCriteria: apiData.meetsJoinCriteria,
    joinCriteriaFailureReasons: apiData.joinCriteriaFailureReasons,
    // Distance from the search origin (user location), returned by search results. Without
    // this the map side panel's footer falls back to "Distance unavailable" even though the
    // server computed it (and the join-criteria reason already quotes it).
    distanceFeet: apiData.distanceFeet,
    joinGeofence: apiData.joinGeofence,
    requiresSubscription: apiData.requiresSubscription,
    // Story access (Backlog 060 §10) and the 059 series block. This transform is an allowlist, so
    // the series banner never saw `series` until it was named here.
    accessLocked: apiData.accessLocked,
    lockReason: apiData.lockReason,
    lockMessage: apiData.lockMessage,
    requiredPlanDisplayName: apiData.requiredPlanDisplayName,
    purchaseOffer: apiData.purchaseOffer ?? null,
    series: apiData.series,
    approxLatitude: apiData.approxLatitude,
    approxLongitude: apiData.approxLongitude,
    // User's relationship to this adventure (bitmask)
    userRelationType: apiData.userRelationType,
    // Folder + owner, which together place the adventure in the Manage browser. This is an
    // allowlist, so anything not named here is dropped before the page ever sees it.
    folderId: apiData.folderId ?? null,
    folderPath: apiData.folderPath,
    ownerEntityId: apiData.ownerEntityId,
    ownerEntityType: apiData.ownerEntityType,
    ownerName: apiData.ownerName,
    ownerAvatarFilePath: apiData.ownerAvatarFilePath,
    // Session-related fields
    isStarted: apiData.isStarted,
    isCompleted: apiData.isCompleted,
    sessions: apiData.sessions,
    totalSessionCount: apiData.totalSessionCount,
    // Management permissions
    canManage: apiData.canManage,
    creatorId: apiData.creatorId,
    // Geocache code fields
    geocacheCode: apiData.geocacheCode,
    geocacheCodeUrl: apiData.geocacheCodeUrl,
    // Moderation features
    hasModerationNodes: apiData.hasModerationNodes,
    // Available group sessions count
    availableGroupSessionCount: apiData.availableGroupSessionCount,
    // Player-activity popularity stats (from search results). Dropped previously, which left
    // both the map side panel's "trending" row and AdventureCard's popularity popover empty.
    uniquePlayersLastHour: apiData.uniquePlayersLastHour,
    uniquePlayersLast24Hours: apiData.uniquePlayersLast24Hours,
    uniquePlayersLastMonth: apiData.uniquePlayersLastMonth,
    uniquePlayersLastYear: apiData.uniquePlayersLastYear,
  }

  return transformed
}

// Helper to clean params for query keys
function cleanParamsForKey(params?: AdventureListParams): AdventureListParams | undefined {
  if (!params) return undefined

  const cleaned: any = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value
    }
  })

  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

// Query keys
export const adventureKeys = {
  all: ['adventures'] as const,
  lists: () => [...adventureKeys.all, 'list'] as const,
  list: (params?: AdventureListParams) => [...adventureKeys.lists(), cleanParamsForKey(params)] as const,
  searches: () => [...adventureKeys.all, 'search'] as const,
  search: (request?: AdventureSearchRequest) => [...adventureKeys.searches(), cleanParamsForKey(request as any)] as const,
  details: () => [...adventureKeys.all, 'detail'] as const,
  detail: (id: string) => [...adventureKeys.details(), id] as const,
  edits: () => [...adventureKeys.all, 'edit'] as const,
  edit: (id: string) => [...adventureKeys.edits(), id] as const,
  // Standalone keys the My Adventures page queries under (not nested below `all`,
  // so `invalidateQueries(lists())` does NOT reach them — invalidate explicitly).
  myAdventures: ['myAdventures'] as const,
  mySilentAdventures: ['mySilentAdventures'] as const,
}

// Invalidate every cached list that should reflect an adventure add/edit/remove,
// including the standalone keys used by the My Adventures page.
function invalidateAdventureLists(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
  queryClient.invalidateQueries({ queryKey: adventureKeys.myAdventures })
  queryClient.invalidateQueries({ queryKey: adventureKeys.mySilentAdventures })
}

// The thumbnail and cover are saved on their own (files/select), not through the settings PATCH, so
// nothing else refreshes the cached adventure after one is chosen. Without this the detail page and
// lists kept the old picture for the 5-minute staleTime, and reopening the editor re-seeded it from
// the stale edit cache: the new picture looked like it had only been added to the Files.
export function invalidateAdventureImages(queryClient: ReturnType<typeof useQueryClient>, id: string) {
  queryClient.invalidateQueries({ queryKey: adventureKeys.edit(id) })
  queryClient.invalidateQueries({ queryKey: adventureKeys.detail(id) })
  queryClient.invalidateQueries({ queryKey: adventureKeys.searches() })
  invalidateAdventureLists(queryClient)
}

// Get list of adventures
export function useAdventures(params?: AdventureListParams) {
  return useQuery({
    queryKey: adventureKeys.list(params),
    queryFn: async () => {
      const result = await adventuresApi.getAdventures(params)

      // Handle array response - transform to PaginatedResponse
      if (Array.isArray(result)) {
        // Transform each adventure to match our expected format
        const transformedItems = result.map(transformAdventure)
        const totalCount = transformedItems.length
        const pageSize = params?.pageSize || 12
        const pageNumber = params?.page || 1
        const totalPages = Math.ceil(totalCount / pageSize)

        return {
          items: transformedItems,
          totalCount,
          pageNumber,
          pageSize,
          totalPages,
          hasNextPage: pageNumber < totalPages,
          hasPreviousPage: pageNumber > 1,
        }
      }

      // Handle wrapped response (ApiResponse<PaginatedResponse<T>>)
      if (result && typeof result === 'object' && 'data' in result) {
        const data = (result as any).data

        // If data.items exists, transform each item
        if (data.items && Array.isArray(data.items)) {
          return {
            ...data,
            items: data.items.map(transformAdventure)
          }
        }

        return data
      }

      // Already in correct format - but still transform items if it's a paginated response
      if (result && typeof result === 'object' && 'items' in result) {
        const paginatedResult = result as any
        return {
          ...paginatedResult,
          items: paginatedResult.items.map(transformAdventure)
        }
      }

      return result
    },
  })
}

// Search adventures with advanced filters
export function useSearchAdventures(searchRequest?: AdventureSearchRequest) {
  return useQuery({
    queryKey: adventureKeys.search(searchRequest),
    queryFn: async () => {
      // If no search request provided, return empty results
      if (!searchRequest) {
        console.log('useSearchAdventures: No search request, returning empty')
        return {
          items: [],
          totalCount: 0,
          pageNumber: 1,
          pageSize: 20,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }
      }

      console.log('useSearchAdventures: Making API call with:', searchRequest)

      try {
        const result = await adventuresApi.searchAdventures(searchRequest)
        console.log('useSearchAdventures: API response:', result)

        let transformedResult

        // Handle array response - transform to PaginatedResponse
        if (Array.isArray(result)) {
          // Transform each adventure to match our expected format
          const transformedItems = result.map(transformAdventure)
          const totalCount = transformedItems.length
          const pageSize = searchRequest?.pageSize || 20
          const pageNumber = searchRequest?.page || 1
          const totalPages = Math.ceil(totalCount / pageSize)

          transformedResult = {
            items: transformedItems,
            totalCount,
            pageNumber,
            pageSize,
            totalPages,
            hasNextPage: pageNumber < totalPages,
            hasPreviousPage: pageNumber > 1,
          }
        }
        // Handle wrapped response (ApiResponse<PaginatedResponse<T>>)
        else if (result && typeof result === 'object' && 'data' in result) {
          const data = (result as any).data

          // If data.items exists, transform each item
          if (data.items && Array.isArray(data.items)) {
            transformedResult = {
              ...data,
              items: data.items.map(transformAdventure)
            }
          } else {
            transformedResult = data
          }
        }
        // Handle response with 'adventures' field (from search endpoint)
        else if (result && typeof result === 'object' && 'adventures' in result) {
          const paginatedResult = result as any
          console.log('useSearchAdventures: Transforming adventures array')
          transformedResult = {
            items: paginatedResult.adventures.map(transformAdventure),
            pageNumber: paginatedResult.page,
            totalCount: paginatedResult.totalCount,
            pageSize: paginatedResult.pageSize,
            totalPages: paginatedResult.totalPages,
            hasNextPage: paginatedResult.hasNextPage,
            hasPreviousPage: paginatedResult.hasPreviousPage,
          }
        }
        // Already in correct format - but still transform items if it's a paginated response
        else if (result && typeof result === 'object' && 'items' in result) {
          const paginatedResult = result as any
          transformedResult = {
            ...paginatedResult,
            items: paginatedResult.items.map(transformAdventure)
          }
        } else {
          transformedResult = result
        }

        // Cache the successful result
        if (transformedResult) {
          // Fire and forget - don't await
          cacheSearchResults(searchRequest, transformedResult).catch((err) => {
            console.warn('Failed to cache search results:', err)
          })
        }

        return transformedResult
      } catch (error) {
        // On error, try to use cached data as fallback
        console.warn('useSearchAdventures: API error, checking cache...', error)
        const cached = await getCachedSearchResults(searchRequest)

        if (cached) {
          console.log('📦 Using cached data as fallback')
          return cached
        }

        // If no cache available, re-throw the error
        throw error
      }
    },
    enabled: !!searchRequest,
    // Keep the previously loaded results on screen while a new query key loads
    // (e.g. GPS drift or a background refetch), so cards swap in place instead of
    // collapsing to skeletons.
    placeholderData: keepPreviousData,
    // Results are good for 5 minutes before a background refresh is warranted.
    staleTime: 5 * 60 * 1000,
    // Keep cached data in React Query for 30 minutes
    gcTime: 1000 * 60 * 30,
    // Refresh search results every 5 minutes
    refetchInterval: 5 * 60 * 1000,
  })
}

/**
 * How many upcoming adventures are near a point.
 *
 * Deliberately not useSearchAdventures: that polls every 30s with staleTime 0, which is
 * senseless for a number that moves on the order of days. This asks for a single row and
 * reads totalCount from the response metadata — the server computes it before paging.
 *
 * Caveat: the count over-reports slightly. The age gate and display-criteria filter
 * decrement totalCount only by what they strip from the returned page, and with
 * pageSize 1 at most one row is ever checked. That's a pre-existing quirk of the
 * endpoint; it's tolerable for a soft nudge, but don't build anything exact on it.
 */
export function useUpcomingCount(lat?: number, lng?: number, enabled = true) {
  return useQuery({
    queryKey: [...adventureKeys.searches(), 'upcoming-count', lat, lng] as const,
    queryFn: async () => {
      const response = await adventuresApi.searchAdventures({
        mode: 'upcoming',
        userLatitude: lat,
        userLongitude: lng,
        radiusMiles: 50,
        page: 1,
        pageSize: 1,
      })
      return (response as unknown as { totalCount?: number })?.totalCount ?? 0
    },
    enabled: enabled && lat != null && lng != null,
    staleTime: 5 * 60 * 1000,
    refetchInterval: false,
  })
}

// Get single adventure (for viewing)
/**
 * One adventure's detail.
 *
 * `viewer` is the viewer's live fix. It is deliberately NOT part of the query key (every cache
 * write and invalidation of the detail uses the bare key); the latest one is read when the query
 * runs, and a new one refetches only when the caller decides it is worth asking again — see
 * `shouldRefetchFrom`. Keying on raw GPS would refetch on every tick.
 */
export function useAdventure(id: string, viewer?: Fix | null) {
  const viewerRef = useRef<Fix | null>(viewer ?? null)
  viewerRef.current = viewer ?? null

  const query = useQuery({
    queryKey: adventureKeys.detail(id),
    queryFn: async () => {
      const result = await adventuresApi.getAdventure(id, viewerRef.current)

      let adventure = result

      // Handle wrapped response (ApiResponse<AdventureDto>)
      if (result && typeof result === 'object' && 'data' in result && result.data) {
        adventure = (result as any).data
      }

      // Transform the adventure to match our expected format
      if (adventure && typeof adventure === 'object') {
        return transformAdventure(adventure)
      }

      return adventure
    },
    enabled: !!id,
  })

  // Ask again once the fix has moved or sharpened enough that the join verdict might change.
  // The first fix usually lands while the initial (position-less) fetch is still out; refetch()
  // cancels that one, whose answer would be stale on arrival.
  const anchorRef = useRef<Fix | null>(null)
  const { refetch } = query
  useEffect(() => {
    if (!viewer || !id) return
    if (!shouldRefetchFrom(anchorRef.current, viewer)) return
    anchorRef.current = viewer
    void refetch()
  }, [viewer, id, refetch])

  return query
}

// Get adventure for editing (includes all editor fields like mainCategory)
export function useAdventureForEdit(id: string) {
  return useQuery({
    queryKey: adventureKeys.edit(id),
    queryFn: async () => {
      const result = await adventuresApi.getAdventureForEdit(id)

      let adventure = result

      // Handle wrapped response (ApiResponse<AdventureDto>)
      if (result && typeof result === 'object' && 'data' in result && result.data) {
        adventure = (result as any).data
      }

      // Transform the adventure to match our expected format
      if (adventure && typeof adventure === 'object') {
        return transformAdventure(adventure)
      }

      return adventure
    },
    enabled: !!id,
  })
}

// Create adventure
export function useCreateAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAdventureRequest) => adventuresApi.createAdventure(data),
    onSuccess: () => {
      invalidateAdventureLists(queryClient)
    },
  })
}

// Update adventure
export function useUpdateAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAdventureRequest | (Omit<UpdateAdventureRequest, 'categoryId'> & { mainCategory?: number }) }) => {
      // Transform mainCategory to categoryId for API compatibility
      // The internal data model uses mainCategory but API expects categoryId
      const apiData: UpdateAdventureRequest = {
        ...data,
        categoryId: 'categoryId' in data ? data.categoryId : (data as any).mainCategory,
      }
      // Remove mainCategory if it was passed (shouldn't be sent to API)
      delete (apiData as any).mainCategory

      const result = await adventuresApi.updateAdventure(id, apiData)

      // Handle wrapped response (ApiResponse<AdventureDto>)
      let adventure = result
      if (result && typeof result === 'object' && 'data' in result && result.data) {
        adventure = (result as any).data
      }

      // Transform the adventure to match our expected format
      if (adventure && typeof adventure === 'object') {
        return transformAdventure(adventure)
      }

      return adventure
    },
    onSuccess: (transformedAdventure, variables) => {
      // Update the cache with the transformed adventure (for both detail and edit)
      if (transformedAdventure) {
        queryClient.setQueryData(
          adventureKeys.detail(variables.id),
          transformedAdventure
        )
        queryClient.setQueryData(
          adventureKeys.edit(variables.id),
          transformedAdventure
        )
      }

      invalidateAdventureLists(queryClient)
    },
  })
}

// Save only the settings that changed (PATCH): the server keeps everything else as saved.
export function usePatchAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, fields }: { id: string; fields: Record<string, unknown> }) => {
      const result: unknown = await adventuresApi.patchAdventure(id, fields)
      // Some responses arrive wrapped (ApiResponse<AdventureDto>).
      const adventure = result && typeof result === 'object' && 'data' in result && result.data ? result.data : result
      return adventure && typeof adventure === 'object' ? transformAdventure(adventure) : undefined
    },
    onSuccess: (transformedAdventure, variables) => {
      if (transformedAdventure) {
        queryClient.setQueryData(adventureKeys.detail(variables.id), transformedAdventure)
        queryClient.setQueryData(adventureKeys.edit(variables.id), transformedAdventure)
      }
      invalidateAdventureLists(queryClient)
    },
  })
}

// Delete adventure
export function useDeleteAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adventuresApi.deleteAdventure(id),
    onSuccess: () => {
      invalidateAdventureLists(queryClient)
    },
  })
}

// Join adventure solo (explicit)
export function useJoinAdventureSolo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, startDateTime, methodUsed }: { id: string; startDateTime?: string; methodUsed?: string }) =>
      adventuresApi.joinAdventureSolo(id, startDateTime, methodUsed),
    onSuccess: (data, { id }) => {
      // Joined here, now: an adventure that ends inside its join still opens on its ending.
      markLiveHere(data?.sessionId)
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Create group and join adventure
export function useCreateGroupJoin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<import('@api/types/adventure').AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.createGroupJoin(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
      // Also invalidate any group-related queries
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
    },
  })
}

// Complete adventure
export function useCompleteAdventure() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => adventuresApi.completeAdventure(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: adventureKeys.lists() })
    },
  })
}

// Join with existing group
export function useJoinWithGroup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.joinWithGroup(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
    },
  })
}

// Join public queue
export function useJoinPublic() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.joinPublic(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
    },
  })
}

// Create invite/public session
export function useCreateInviteSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ adventureId, request }: { adventureId: string; request: Omit<AdventureJoinRequest, 'adventureId'> }) =>
      adventuresApi.createSession(adventureId, request),
    onSuccess: (_, { adventureId }) => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.detail(adventureId) })
    },
  })
}

// Join an existing public session
export function useJoinPublicSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) =>
      adventuresApi.joinPublicSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adventureKeys.all })
    },
  })
}

// Get public sessions for an adventure
export function usePublicSessions(adventureId: string, enabled = true) {
  return useQuery({
    queryKey: [...adventureKeys.detail(adventureId), 'public-sessions'] as const,
    queryFn: () => adventuresApi.getPublicSessions(adventureId),
    enabled: !!adventureId && enabled,
    staleTime: 15000,
  })
}

/**
 * Fetch a specific handful of adventures by id, in the order asked for.
 *
 * For the map's co-located list. A marker knows an id, a position and a category and nothing
 * else — deliberately, since the map is moving to slim pin tiles where that is genuinely all
 * there is — so anything that needs to show a row has to ask for one. The set is small by
 * definition: it is what would not come apart under one pin.
 */
export function useAdventuresByIds(adventureIds: string[] | undefined) {
  const ids = adventureIds ?? []
  return useQuery({
    queryKey: ['adventures', 'by-ids', ids],
    queryFn: async () => {
      const response = await adventuresApi.searchAdventures({
        adventureIds: ids,
        pageSize: Math.max(ids.length, 1),
        page: 1,
      })
      const list =
        (response as { adventures?: unknown[] }).adventures ??
        (response as { items?: unknown[] }).items ??
        []
      // Search ranks; the caller's order is the one the pins were read in, so reapply it.
      const order = new Map(ids.map((id, index) => [id, index]))
      return [...list]
        .map(transformAdventure)
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0))
    },
    enabled: ids.length > 0,
    staleTime: 30000,
  })
}

// Get adventure categories
export function useAdventureCategories() {
  return useQuery({
    queryKey: ['adventure-categories'],
    queryFn: async () => {
      const result = await adventuresApi.getCategories()

      // Handle wrapped response (ApiResponse<AdventureCategoryDto[]>)
      if (result && typeof result === 'object' && 'data' in result) {
        return (result as any).data
      }

      return result
    },
    staleTime: 1000 * 60 * 60, // Categories don't change often, cache for 1 hour
  })
}
